import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import {
  formatDate,
  formatDateTime,
  getActiveStateLabel,
  getCouponScopeLabel,
  getCouponTypeLabel,
  translateApiMessage,
} from '../utils/dashboardTranslations';

const initialForm = {
  code: '',
  description: '',
  discountType: 'Percentage',
  discountValue: '',
  scope: 'All',
  vendorId: '',
  productId: '',
  vendorCategoryId: '',
  minimumOrderAmount: '',
  maxDiscountAmount: '',
  startDate: '',
  expiryDate: '',
  maxUsageCount: '',
  maxUsagePerUser: '',
  isActive: true,
};

function Coupons() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [productVendorFilter, setProductVendorFilter] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('');
  const [selectedUsage, setSelectedUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const normalizedCoupons = useMemo(() => coupons || [], [coupons]);
  const productCategoryOptions = useMemo(() => {
    const categoryMap = new Map();

    (products || []).forEach((product) => {
      const vendorId = String(product?.vendorId ?? product?.vendor?.id ?? '');
      if (productVendorFilter && vendorId !== productVendorFilter) {
        return;
      }

      const categoryId =
        product?.vendorCategoryId ??
        product?.vendorCategory?.id ??
        product?.vendor?.vendorCategoryId ??
        product?.vendor?.vendorCategory?.id;

      if (!categoryId) {
        return;
      }

      const categoryName =
        product?.vendorCategory?.name ??
        product?.vendor?.vendorCategory?.name ??
        categories.find((category) => Number(category.id) === Number(categoryId))?.name;

      if (!categoryName) {
        return;
      }

      categoryMap.set(String(categoryId), {
        id: String(categoryId),
        name: categoryName,
      });
    });

    return Array.from(categoryMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'ar'));
  }, [products, categories, productVendorFilter]);

  const filteredProducts = useMemo(() => {
    return (products || []).filter((product) => {
      const vendorId = String(product?.vendorId ?? product?.vendor?.id ?? '');
      const categoryId = String(
        product?.vendorCategoryId ??
        product?.vendorCategory?.id ??
        product?.vendor?.vendorCategoryId ??
        product?.vendor?.vendorCategory?.id ??
        ''
      );

      const matchesVendor = productVendorFilter ? vendorId === productVendorFilter : true;
      const matchesCategory = productCategoryFilter ? categoryId === productCategoryFilter : true;

      return matchesVendor && matchesCategory;
    });
  }, [products, productVendorFilter, productCategoryFilter]);

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    if (!productCategoryFilter) {
      return;
    }

    const isCurrentCategoryAvailable = productCategoryOptions.some(
      (category) => category.id === productCategoryFilter
    );

    if (!isCurrentCategoryAvailable) {
      setProductCategoryFilter('');
      setForm((prev) => ({ ...prev, productId: '' }));
    }
  }, [productCategoryFilter, productCategoryOptions]);

  const fetchLookups = async () => {
    try {
      const [vendorsRes, productsRes, categoriesRes] = await Promise.all([
        api.get('/Vendor'),
        api.get('/Product'),
        api.get('/VendorCategory'),
      ]);

      setVendors(Array.isArray(vendorsRes.data) ? vendorsRes.data : vendorsRes.data?.vendors || []);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : productsRes.data?.items || []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : categoriesRes.data?.categories || []);
    } catch (error) {
      console.error('Error loading coupon lookups:', error);
      alert('فشل تحميل بيانات القوائم المساعدة');
    }
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.discountType = typeFilter;

      const response = await api.get('/admin/coupons', { params });
      setCoupons(response.data?.items || []);
    } catch (error) {
      console.error('Error loading coupons:', error);
      alert('فشل تحميل الكوبونات');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditId(null);
    setProductVendorFilter('');
    setProductCategoryFilter('');
  };

  const generateRandomCode = () => {
    const code = `SAVE${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setForm((prev) => ({ ...prev, code }));
  };

  const validateForm = () => {
    if (!form.code.trim()) return 'رمز الكوبون مطلوب';
    if (form.startDate && form.expiryDate && new Date(form.expiryDate) <= new Date(form.startDate)) {
      return 'تاريخ الانتهاء يجب أن يكون بعد تاريخ البدء';
    }
    if (form.discountType !== 'FreeDelivery' && (!form.discountValue || Number(form.discountValue) <= 0)) {
      return 'قيمة الخصم مطلوبة ويجب أن تكون أكبر من 0';
    }
    if (form.discountType === 'Percentage' && Number(form.discountValue) > 100) {
      return 'نسبة الخصم لا يمكن أن تتجاوز 100';
    }
    if (form.scope === 'Vendor' && !form.vendorId) return 'يرجى اختيار بائع';
    if (form.scope === 'Product' && !form.productId) return 'يرجى اختيار منتج';
    if (form.scope === 'Category' && !form.vendorCategoryId) return 'يرجى اختيار فئة';
    return null;
  };

  const buildPayload = () => ({
    code: form.code.trim().toUpperCase(),
    description: form.description || null,
    discountType: form.discountType,
    discountValue: form.discountType === 'FreeDelivery' ? null : Number(form.discountValue || 0),
    scope: form.scope,
    vendorId: form.scope === 'Vendor' ? Number(form.vendorId) : null,
    productId: form.scope === 'Product' ? Number(form.productId) : null,
    vendorCategoryId: form.scope === 'Category' ? Number(form.vendorCategoryId) : null,
    minimumOrderAmount: form.minimumOrderAmount ? Number(form.minimumOrderAmount) : null,
    maxDiscountAmount: form.discountType === 'Percentage' && form.maxDiscountAmount ? Number(form.maxDiscountAmount) : null,
    startDate: form.startDate || null,
    expiryDate: form.expiryDate || null,
    maxUsageCount: form.maxUsageCount ? Number(form.maxUsageCount) : null,
    maxUsagePerUser: form.maxUsagePerUser ? Number(form.maxUsagePerUser) : null,
    isActive: !!form.isActive,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      if (editId) {
        await api.put(`/admin/coupons/${editId}`, payload);
        alert('تم تحديث الكوبون بنجاح');
      } else {
        await api.post('/admin/coupons', payload);
        alert('تم إنشاء الكوبون بنجاح');
      }
      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);
      alert(
        translateApiMessage(error?.response?.data?.message || error?.response?.data) || 'فشل حفظ الكوبون'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (coupon) => {
    const selectedProduct = products.find((product) => Number(product.id) === Number(coupon.productId));
    const selectedProductVendorId = String(selectedProduct?.vendorId ?? selectedProduct?.vendor?.id ?? '');
    const selectedProductCategoryId = String(
      selectedProduct?.vendorCategoryId ??
      selectedProduct?.vendorCategory?.id ??
      selectedProduct?.vendor?.vendorCategoryId ??
      selectedProduct?.vendor?.vendorCategory?.id ??
      ''
    );

    setEditId(coupon.id);
    setForm({
      code: coupon.code || '',
      description: coupon.description || '',
      discountType: coupon.discountType || 'Percentage',
      discountValue: coupon.discountValue ?? '',
      scope: coupon.scope || 'All',
      vendorId: coupon.vendorId ?? '',
      productId: coupon.productId ?? '',
      vendorCategoryId: coupon.vendorCategoryId ?? '',
      minimumOrderAmount: coupon.minimumOrderAmount ?? '',
      maxDiscountAmount: coupon.maxDiscountAmount ?? '',
      startDate: coupon.startDate ? String(coupon.startDate).slice(0, 10) : '',
      expiryDate: coupon.expiryDate ? String(coupon.expiryDate).slice(0, 10) : '',
      maxUsageCount: coupon.maxUsageCount ?? '',
      maxUsagePerUser: coupon.maxUsagePerUser ?? '',
      isActive: coupon.isActive !== false,
    });
    setProductVendorFilter(selectedProductVendorId);
    setProductCategoryFilter(selectedProductCategoryId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeactivate = async (couponId) => {
    if (!window.confirm('هل تريد تعطيل هذا الكوبون؟')) return;
    try {
      await api.delete(`/admin/coupons/${couponId}`);
      fetchCoupons();
    } catch (error) {
      console.error('Error deactivating coupon:', error);
      alert('فشل تعطيل الكوبون');
    }
  };

  const openUsage = async (couponId) => {
    try {
      setUsageLoading(true);
      setSelectedUsage(null);
      const response = await api.get(`/admin/coupons/${couponId}/usage`);
      setSelectedUsage(response.data);
    } catch (error) {
      console.error('Error loading usage:', error);
      alert('فشل تحميل استخدامات الكوبون');
    } finally {
      setUsageLoading(false);
    }
  };

  const getStatusBadge = (coupon) => {
    if (!coupon.isActive) return <span className="badge bg-secondary">{getActiveStateLabel(false)}</span>;
    if (coupon.expiryDate && new Date(coupon.expiryDate) < new Date()) {
      return <span className="badge bg-danger">منتهي</span>;
    }
    return <span className="badge bg-success">{getActiveStateLabel(true)}</span>;
  };

  return (
    <div>
      <h4 className="mb-3">إدارة الكوبونات</h4>

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{editId ? 'تعديل كوبون' : 'إضافة كوبون جديد'}</strong>
          {editId && (
            <button className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
              إلغاء التعديل
            </button>
          )}
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">الكود</label>
                <div className="input-group">
                  <input
                    className="form-control"
                    value={form.code}
                    onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                    required
                  />
                  <button type="button" className="btn btn-outline-primary" onClick={generateRandomCode}>
                    توليد
                  </button>
                </div>
              </div>

              <div className="col-md-4">
                <label className="form-label">نوع الخصم</label>
                <select
                  className="form-select"
                  value={form.discountType}
                  onChange={(e) => setForm((p) => ({ ...p, discountType: e.target.value }))}
                >
                  <option value="Percentage">نسبة مئوية</option>
                  <option value="FixedAmount">مبلغ ثابت</option>
                  <option value="FreeDelivery">توصيل مجاني</option>
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">قيمة الخصم</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.discountValue}
                  onChange={(e) => setForm((p) => ({ ...p, discountValue: e.target.value }))}
                  disabled={form.discountType === 'FreeDelivery'}
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">الوصف</label>
                <input
                  className="form-control"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">النطاق</label>
                <select
                  className="form-select"
                  value={form.scope}
                  onChange={(e) => {
                    const nextScope = e.target.value;
                    setForm((p) => ({
                      ...p,
                      scope: nextScope,
                      vendorId: nextScope === 'Vendor' ? p.vendorId : '',
                      productId: nextScope === 'Product' ? p.productId : '',
                      vendorCategoryId: nextScope === 'Category' ? p.vendorCategoryId : '',
                    }));

                    if (nextScope !== 'Product') {
                      setProductVendorFilter('');
                      setProductCategoryFilter('');
                    }
                  }}
                >
                  <option value="All">كل الطلبات</option>
                  <option value="Vendor">بائع محدد</option>
                  <option value="Product">منتج محدد</option>
                  <option value="Category">فئة محددة</option>
                </select>
              </div>

              {form.scope === 'Vendor' && (
                <div className="col-md-3">
                  <label className="form-label">البائع</label>
                  <select
                    className="form-select"
                    value={form.vendorId}
                    onChange={(e) => setForm((p) => ({ ...p, vendorId: e.target.value }))}
                    required
                  >
                    <option value="">اختر</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {form.scope === 'Product' && (
                <>
                  <div className="col-md-3">
                    <label className="form-label">تصفية حسب البائع</label>
                    <select
                      className="form-select"
                      value={productVendorFilter}
                      onChange={(e) => {
                        setProductVendorFilter(e.target.value);
                        setForm((p) => ({ ...p, productId: '' }));
                      }}
                    >
                      <option value="">كل البائعين</option>
                      {vendors.map((vendor) => (
                        <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">تصفية حسب الفئة</label>
                    <select
                      className="form-select"
                      value={productCategoryFilter}
                      onChange={(e) => {
                        setProductCategoryFilter(e.target.value);
                        setForm((p) => ({ ...p, productId: '' }));
                      }}
                    >
                      <option value="">كل الفئات</option>
                      {productCategoryOptions.map((category) => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">المنتج</label>
                    <select
                      className="form-select"
                      value={form.productId}
                      onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value }))}
                      required
                    >
                      <option value="">اختر</option>
                      {filteredProducts.map((product) => {
                        const vendorName = product?.vendor?.name;
                        const categoryName =
                          product?.vendorCategory?.name ||
                          product?.vendor?.vendorCategory?.name;

                        return (
                          <option key={product.id} value={product.id}>
                            {product.name}
                            {vendorName ? ` - ${vendorName}` : ''}
                            {categoryName ? ` - ${categoryName}` : ''}
                          </option>
                        );
                      })}
                    </select>
                    <small className="text-muted">
                      المنتجات المعروضة: {filteredProducts.length}
                    </small>
                  </div>
                </>
              )}

              {form.scope === 'Category' && (
                <div className="col-md-3">
                  <label className="form-label">الفئة</label>
                  <select
                    className="form-select"
                    value={form.vendorCategoryId}
                    onChange={(e) => setForm((p) => ({ ...p, vendorCategoryId: e.target.value }))}
                    required
                  >
                    <option value="">اختر</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-md-3">
                <label className="form-label">الحد الأدنى للطلب</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.minimumOrderAmount}
                  onChange={(e) => setForm((p) => ({ ...p, minimumOrderAmount: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">أقصى خصم</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.maxDiscountAmount}
                  onChange={(e) => setForm((p) => ({ ...p, maxDiscountAmount: e.target.value }))}
                  disabled={form.discountType !== 'Percentage'}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">تاريخ البدء</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.startDate}
                  onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">تاريخ الانتهاء</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.expiryDate}
                  onChange={(e) => setForm((p) => ({ ...p, expiryDate: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">أقصى عدد استخدامات</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.maxUsageCount}
                  onChange={(e) => setForm((p) => ({ ...p, maxUsageCount: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">أقصى استخدام لكل مستخدم</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.maxUsagePerUser}
                  onChange={(e) => setForm((p) => ({ ...p, maxUsagePerUser: e.target.value }))}
                />
              </div>

              <div className="col-md-3 d-flex align-items-end">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                  />
                  <label className="form-check-label">فعّال</label>
                </div>
              </div>

              <div className="col-md-12 text-end">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'جاري الحفظ...' : editId ? 'حفظ التعديل' : 'إنشاء الكوبون'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <strong>قائمة الكوبونات</strong>
          <div className="d-flex gap-2">
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">الكل</option>
              <option value="active">الفعّالة</option>
              <option value="expired">المنتهية</option>
            </select>
            <select className="form-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">كل الأنواع</option>
              <option value="Percentage">نسبة مئوية</option>
              <option value="FixedAmount">مبلغ ثابت</option>
              <option value="FreeDelivery">توصيل مجاني</option>
            </select>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">جاري التحميل...</div>
          ) : normalizedCoupons.length === 0 ? (
            <div className="text-center text-muted py-4">لا توجد كوبونات</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-striped">
                <thead className="table-light">
                  <tr>
                    <th>الكود</th>
                    <th>النوع</th>
                    <th>القيمة</th>
                    <th>النطاق</th>
                    <th>الاستخدام</th>
                    <th>الحالة</th>
                    <th>تاريخ الانتهاء</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {normalizedCoupons.map((coupon) => (
                    <tr key={coupon.id}>
                      <td className="fw-bold">{coupon.code}</td>
                      <td>{getCouponTypeLabel(coupon.discountType)}</td>
                      <td>{coupon.discountType === 'FreeDelivery' ? 'توصيل مجاني' : coupon.discountValue}</td>
                      <td>{getCouponScopeLabel(coupon.scope)}</td>
                      <td>{coupon.currentUsageCount || 0}/{coupon.maxUsageCount || '∞'}</td>
                      <td>{getStatusBadge(coupon)}</td>
                      <td>{formatDate(coupon.expiryDate)}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-primary" onClick={() => handleEdit(coupon)}>تعديل</button>
                          <button className="btn btn-outline-info" onClick={() => openUsage(coupon.id)}>الاستخدامات</button>
                          <button className="btn btn-outline-danger" onClick={() => handleDeactivate(coupon.id)}>تعطيل</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {usageLoading && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center py-5">جاري تحميل الإحصائيات...</div>
            </div>
          </div>
        </div>
      )}

      {selectedUsage && !usageLoading && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">استخدامات الكوبون: {selectedUsage?.coupon?.code}</h5>
                <button className="btn-close" onClick={() => setSelectedUsage(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3 d-flex gap-3 flex-wrap">
                  <span className="badge bg-primary">الاستخدام الحالي: {selectedUsage?.coupon?.currentUsageCount || 0}</span>
                  <span className="badge bg-success">عدد العمليات: {(selectedUsage?.usage || []).length}</span>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>المستخدم</th>
                        <th>الطلب</th>
                        <th>الخصم</th>
                        <th>التاريخ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedUsage?.usage || []).map((u) => (
                        <tr key={u.id}>
                          <td>{u.userName || `مستخدم #${u.userId}`}</td>
                          <td>{u.orderNumber || `#${u.orderId}`}</td>
                          <td>{u.discountApplied}</td>
                          <td>{formatDateTime(u.usedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Coupons;
